import { toast } from 'sonner'

import { CLAIM_OUTCOME_LABELS, CLAIM_STATUS_LABELS } from '@/components/ClaimsTable'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ClaimOutcome, ClaimStatus, type InternalClaim, useSetClaimStatus } from '@/models/claim'

// Legacy states with no step on the customer's timeline, so staff cannot pick them.
const LEGACY = [ClaimStatus.IN_REVIEW, ClaimStatus.INFO_NEEDED]
const SELECTABLE = Object.values(ClaimStatus).filter((status) => !LEGACY.includes(status))

/**
 * The carrier dialog is owned by the page, not the row: a status change refetches
 * the list, and under a status filter that unmounts the row mid-typing.
 */
export function ClaimLifecycleActions({
  claim,
  onOpenCarrier,
  onAskOutcome,
}: {
  claim: InternalClaim
  onOpenCarrier: (claim: InternalClaim) => void
  onAskOutcome: (claim: InternalClaim) => void
}) {
  const setStatus = useSetClaimStatus()
  const closed = claim.status === ClaimStatus.CLOSED

  function save(
    args: { status: ClaimStatus; outcome?: ClaimOutcome },
    success: string,
    thenAskCarrier = false
  ) {
    setStatus.mutate(
      { id: claim.id, ...args },
      {
        onSuccess: (updated) => {
          toast.success(success)
          // Opened on the saved claim: the move may have cleared details the row still holds.
          if (thenAskCarrier) onOpenCarrier(updated)
          // Under a status filter this row is already gone, taking its selector with it.
          if (updated.status === ClaimStatus.CLOSED && !updated.outcome) onAskOutcome(updated)
        },
        onError: () => toast.error('That change did not save.'),
      }
    )
  }

  return (
    // Wraps in a phone-width card; in the table, fixed tracks line every row's selects up.
    <div className="flex flex-wrap items-center gap-2 lg:grid lg:grid-cols-[180px_140px]">
      <Select
        value={claim.status}
        disabled={setStatus.isPending}
        onValueChange={(status) => {
          // The reference and the adjuster come from the carrier; nothing derives them.
          const next = status as ClaimStatus
          const ask =
            (next === ClaimStatus.FILED && !claim.carrierReference) ||
            (next === ClaimStatus.OPEN_WITH_CARRIER && !claim.adjusterName)
          save({ status: next }, `Moved to ${CLAIM_STATUS_LABELS[next]}.`, ask)
        }}
      >
        <SelectTrigger size="sm" className="w-[180px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SELECTABLE.map((status) => (
            <SelectItem key={status} value={status}>
              {CLAIM_STATUS_LABELS[status]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {closed ? (
        <Select
          value={claim.outcome ?? ''}
          disabled={setStatus.isPending}
          onValueChange={(outcome) =>
            save(
              { status: ClaimStatus.CLOSED, outcome: outcome as ClaimOutcome },
              `Closed as ${CLAIM_OUTCOME_LABELS[outcome as ClaimOutcome].toLowerCase()}.`
            )
          }
        >
          <SelectTrigger size="sm" className="w-[140px]">
            <SelectValue placeholder="Outcome…" />
          </SelectTrigger>
          <SelectContent>
            {Object.values(ClaimOutcome).map((outcome) => (
              <SelectItem key={outcome} value={outcome}>
                {CLAIM_OUTCOME_LABELS[outcome]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span aria-hidden className="max-lg:hidden" />
      )}
    </div>
  )
}
